import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  Avatar,
  IconButton,
  Rating,
  Skeleton,
  useTheme,
  alpha,
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import listingService from '../../../services/listingService';
import productService from '../../../services/productService';
import { useAuth } from '../../../context/AuthContext';
import { useNotifications } from '../../../context/NotificationContext';
import { useFavorites } from '../../../context/FavoritesContext';
import { getCurrencySymbol } from '../../../utils/currencyUtils';

// ─── URL helpers ──────────────────────────────────────────────────────────────
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';
const BASE_URL = API_URL.replace('/api/v1', '');

const normalizeUrl = (value) => {
  if (!value) return null;
  if (value.startsWith('http')) return value;
  return `${BASE_URL}${value}`;
};

const normalizePerson = (person) => {
  if (!person) return null;
  const name =
    person.name ||
    [person.first_name, person.last_name].filter(Boolean).join(' ') ||
    person.email ||
    null;
  // Supports both camelCase (JS/frontend) and snake_case (raw Sequelize response)
  const picRaw =
    person.profilePicture ||
    person.profile_picture ||
    person.avatar ||
    person.avatarUrl ||
    person.avatar_url ||
    null;
  const pic = normalizeUrl(picRaw);
  return {
    ...person,
    name,
    profilePicture: pic,
    avatar: pic,
  };
};

// Returns only the FIRST image URL (single lazy <img> replaces heavy carousel)
const getFirstImage = (listing, isProduct = false) => {
  const main = normalizeUrl(listing.mainImageUrl);
  if (main) return main;

  const raw = Array.isArray(listing.images) ? listing.images : [];
  const first = raw[0];
  if (first) {
    return typeof first === 'string'
      ? normalizeUrl(first)
      : normalizeUrl(first?.image_url || first?.url);
  }

  if (!isProduct && Array.isArray(listing.imageUrls) && listing.imageUrls[0]) {
    return normalizeUrl(listing.imageUrls[0]);
  }
  return null;
};

const normalizeOwner = (raw) =>
  normalizePerson(
    raw.owner || raw.user || raw.User || raw.createdBy || raw.seller || null
  );

// ─── Photo-priority sort ──────────────────────────────────────────────────────
/**
 * Trie les items : ceux avec au moins une image en premier.
 * Appliqué AVANT diversify pour que le round-robin pioche les items avec photo d'abord.
 */
const sortByPhotoFirst = (items, isProduct = false) => {
  return [...items].sort((a, b) => {
    const aHas = !!getFirstImage(a, isProduct);
    const bHas = !!getFirstImage(b, isProduct);
    if (aHas && !bHas) return -1;
    if (!aHas && bHas) return 1;
    return 0;
  });
};

// ─── Category-diversity algorithm ─────────────────────────────────────────────
/**
 * From a pool of items, select `count` items maximising category diversity.
 * Each item must expose a `category_id`, `categoryId`, `category?.id` or similar.
 * Strategy: group by category, then round-robin across groups.
 */
const diversify = (items, count) => {
  const getCatKey = (item) =>
    item.category_id ||
    item.categoryId ||
    item.category?.id ||
    item.category?.slug ||
    item.category_slug ||
    'unknown';

  const groups = new Map();
  for (const item of items) {
    const key = getCatKey(item);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  }

  const buckets = Array.from(groups.values());
  const result = [];
  let i = 0;
  while (result.length < count && buckets.some((b) => i < b.length)) {
    for (const bucket of buckets) {
      if (result.length >= count) break;
      if (i < bucket.length) result.push(bucket[i]);
    }
    i++;
  }
  return result;
};

// ─── Price helper ─────────────────────────────────────────────────────────────
// Same logic as SearchResultCard.inlinePrice — respects currency, booking_mode
// (slot vs daily/hourly/weekly/monthly) and falls back gracefully.
const _toNum = (v) => {
  if (v === null || v === undefined || v === '') return 0;
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  const p = Number.parseFloat(String(v).replace(',', '.'));
  return Number.isFinite(p) ? p : 0;
};

const getDisplayPrice = (item, isProduct, t) => {
  const symbol = getCurrencySymbol(item.currency || 'EUR');

  if (isProduct) {
    const cents = _toNum(item.price_cents);
    return cents > 0 ? `${Math.round(cents / 100)} ${symbol}` : null;
  }

  // Slot-based pricing
  const isSlot =
    item.booking_mode === 'time_slot' ||
    item.booking_mode === 'time_slot_capacity';
  if (isSlot && item.booking_settings) {
    const slotPrice = _toNum(
      item.booking_settings.price_per_slot || item.booking_settings.base_price
    );
    if (slotPrice > 0) return `${Math.round(slotPrice)} ${symbol}/slot`;
  }

  // Daily / hourly / weekly / monthly — pick the first non-zero value
  const fallbackDaily =
    _toNum(item.price_cents) > 0 ? _toNum(item.price_cents) / 100 : 0;
  const best = [
    {
      v: _toNum(item.price_daily) || fallbackDaily,
      period: t('listing.price.period.daily.short', '/j'),
    },
    { v: _toNum(item.price_hourly), period: t('listing.price.period.hourly.short', '/h') },
    { v: _toNum(item.price_weekly), period: t('listing.price.period.weekly.short', '/s') },
    { v: _toNum(item.price_monthly), period: t('listing.price.period.monthly.short', '/m') },
  ].find((p) => p.v > 0);

  if (!best) return null;
  return `${Math.round(best.v)} ${symbol}${best.period}`;
};

// ─── Mini Card ────────────────────────────────────────────────────────────────
const MiniCard = React.memo(({ item, isProduct }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showNotification } = useNotifications();
  const { isFavorite: isItemFavorite, isPending: isFavoritePending, toggleFavorite } = useFavorites();
  const [hovered, setHovered] = useState(false);

  const favoriteType = isProduct ? 'product' : 'listing';
  const isFavorite = isItemFavorite(favoriteType, item.id);
  const favoritePending = isFavoritePending(favoriteType, item.id);

  const imgSrc = useMemo(() => getFirstImage(item, isProduct), [item, isProduct]);
  const owner = useMemo(() => normalizeOwner(item), [item]);
  const price = useMemo(() => getDisplayPrice(item, isProduct, t), [item, isProduct, t]);

  const rating = parseFloat(item.rating_avg ?? item.rating ?? 0) || 0;
  const reviewCount = parseInt(item.review_count ?? item.rating_count ?? 0, 10) || 0;

  const location = useMemo(() => {
    const city = item.city || item.pickup_location?.city || '';
    const region = item.region || item.country_code || '';
    return [city, region].filter(Boolean).join(', ') || '';
  }, [item]);

  const handleClick = useCallback(() => {
    if (isProduct) {
      navigate(`/product/${item.id}/order`);
    } else {
      navigate(`/view-listing/${item.id}`);
    }
  }, [isProduct, item.id, navigate]);

  const handleFavoriteToggle = useCallback(async (e) => {
    e.stopPropagation();
    if (!user) {
      showNotification(t('search.favorite.loginRequired'), 'info');
      return;
    }
    try {
      const result = await toggleFavorite({ type: favoriteType, targetId: item.id });
      if (!result?.ignored) {
        showNotification(
          result?.isFavorite
            ? t('search.favorite.added')
            : t('search.favorite.removed'),
          'success'
        );
      }
    } catch (error) {
      if (error?.code === 'AUTH_REQUIRED') {
        showNotification(t('search.favorite.loginRequired'), 'info');
      } else {
        showNotification(
          error?.response?.data?.message || t('search.favorite.error', 'Impossible de mettre à jour vos favoris'),
          'error'
        );
      }
    }
  }, [user, favoriteType, item.id, toggleFavorite, showNotification, t]);

  return (
    <Card
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        borderRadius: 3,
        border: '1px solid #ececec',
        boxShadow: hovered
          ? '0 10px 24px rgba(0,0,0,0.12)'
          : '0 2px 10px rgba(0,0,0,0.06)',
        transition: 'all 0.2s ease',
        background: '#fff',
        overflow: 'hidden',
        cursor: 'pointer',
        transform: hovered ? 'translateY(-2px)' : 'translateY(0)',
        '&:hover': { boxShadow: '0 12px 26px rgba(0,0,0,0.14)' },
      }}
    >
      {/* Image zone – single lazy <img>, no carousel overhead */}
      <Box sx={{ position: 'relative', width: '100%', aspectRatio: '4 / 3', overflow: 'hidden', bgcolor: 'grey.100' }}>
        {imgSrc ? (
          <Box
            component="img"
            src={imgSrc}
            alt={item.title}
            loading="lazy"
            decoding="async"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
              transition: 'transform 0.3s ease',
              ...(hovered ? { transform: 'scale(1.04)' } : {}),
            }}
          />
        ) : (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'grey.400', fontSize: '2rem' }}>
            📷
          </Box>
        )}

        {/* Favorite toggle — vrai système */}
        <IconButton
          size="small"
          disabled={favoritePending}
          onClick={handleFavoriteToggle}
          sx={{ position: 'absolute', top: 8, right: 8, zIndex: 3, backgroundColor: 'rgba(255,255,255,0.92)', width: 32, height: 32, '&:hover': { backgroundColor: 'rgba(255,255,255,1)' } }}
        >
          {isFavorite ? <FavoriteIcon sx={{ color: '#ff385c', fontSize: 18 }} /> : <FavoriteBorderIcon sx={{ fontSize: 18 }} />}
        </IconButton>

        {/* Product badge */}
        {isProduct && (
          <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 3, backgroundColor: alpha(theme.palette.success.main, 0.9), color: '#fff', px: 1, py: 0.25, borderRadius: 1, fontSize: '0.65rem', fontWeight: 700, letterSpacing: 0.3 }}>
            {t('search.type.products', 'Produit')}
          </Box>
        )}
      </Box>

      {/* Info zone */}
      <Box sx={{ p: 1.25 }}>
        {/* Title + Price */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 1,
            mb: 0.25,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, flex: 1, minWidth: 0, lineHeight: 1.3 }}
            noWrap
          >
            {item.title}
          </Typography>
          {price && (
            <Typography
              variant="subtitle2"
              fontWeight={800}
              color={isProduct ? 'success.main' : 'primary.main'}
              noWrap
              sx={{ flexShrink: 0, lineHeight: 1.3, fontSize: '0.8rem' }}
            >
              {price}
            </Typography>
          )}
        </Box>

        {/* Location */}
        {location && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {location}
          </Typography>
        )}

        {/* Rating */}
        {rating > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
            <Rating value={rating} size="small" readOnly precision={0.5} />
            <Typography variant="caption" color="text.secondary">
              ({reviewCount})
            </Typography>
          </Box>
        )}

        {/* Avatar + View button */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <Avatar
              src={owner?.profilePicture || undefined}
              alt={owner?.name || ''}
              sx={{ width: 26, height: 26, bgcolor: 'primary.main' }}
            >
              {owner?.name ? owner.name[0].toUpperCase() : <PersonIcon sx={{ fontSize: '0.9rem' }} />}
            </Avatar>
            <Typography variant="caption" noWrap sx={{ fontWeight: 600, maxWidth: 80 }}>
              {owner?.name || t('listing.owner.anonymous', 'Vendeur')}
            </Typography>
          </Box>

          <IconButton
            size="small"
            color={isProduct ? 'success' : 'primary'}
            onClick={(e) => { e.stopPropagation(); handleClick(); }}
            aria-label={isProduct ? t('search.product.order', 'Commander') : t('search.view', 'Voir')}
            sx={{ width: 32, height: 32, borderRadius: '50%' }}
          >
            {isProduct ? <ShoppingCartIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Box>
      </Box>
    </Card>
  );
});

// ─── Card Skeleton ─────────────────────────────────────────────────────────────
const CardSkeleton = () => (
  <Box>
    <Skeleton variant="rectangular" width="100%" sx={{ aspectRatio: '4/3', borderRadius: 3 }} />
    <Box sx={{ pt: 1 }}>
      <Skeleton variant="text" width="80%" />
      <Skeleton variant="text" width="50%" />
      <Skeleton variant="text" width="60%" />
    </Box>
  </Box>
);

// ─── Section header ────────────────────────────────────────────────────────────
const SectionHeader = ({ title, link }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 2,
      }}
    >
      <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      <Button
        size="small"
        endIcon={<ArrowForwardIcon />}
        onClick={() => navigate(link)}
        sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem' }}
      >
        {t('homepage.viewAll', 'Voir tout')}
      </Button>
    </Box>
  );
};

// ─── 6-item grid ─────────────────────────────────────────────────────────────
// Desktop: 3 cols × 2 rows  |  Mobile: 2 cols × 3 rows (no 2+1 orphan)
const SixItemGrid = ({ items, isProduct, loading }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
      gap: { xs: 1.5, sm: 2 },
    }}
  >
    {loading
      ? Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)
      : items.map((item) => (
          <MiniCard key={item.id} item={item} isProduct={isProduct} />
        ))}
  </Box>
);

// ─── Lazy section (IntersectionObserver) ─────────────────────────────────────
const LazySection = ({ children, fallback }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return <div ref={ref}>{visible ? children : fallback}</div>;
};

// ─── Main Component ───────────────────────────────────────────────────────────
const HomeFeaturedSection = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [listings, setListings] = useState([]);
  const [listingsLoading, setListingsLoading] = useState(true);

  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  // Products fetched AFTER listings to avoid simultaneous heavy requests
  const [loadProducts, setLoadProducts] = useState(false);

  // Fetch 12 listings (diversify down to 6)
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await listingService.getPopularListings({ limit: 12 });
        if (!cancelled) {
          const raw = res?.listings || res?.data || res || [];
          const filtered = (Array.isArray(raw) ? raw : []).filter(
            (l) => l.offer_type !== 'product' && !l.booking_settings?.is_product_order
          );
          setListings(diversify(sortByPhotoFirst(filtered, false), 6));
        }
      } catch {
        if (!cancelled) setListings([]);
      } finally {
        if (!cancelled) {
          setListingsLoading(false);
          setLoadProducts(true); // trigger products fetch
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // Fetch products only after listings are done
  useEffect(() => {
    if (!loadProducts) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await productService.search({ limit: 12, status: 'active' });
        if (!cancelled) {
          const raw = res?.products || res?.data || res || [];
          setProducts(diversify(sortByPhotoFirst(Array.isArray(raw) ? raw : [], true), 6));
        }
      } catch {
        if (!cancelled) setProducts([]);
      } finally {
        if (!cancelled) setProductsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [loadProducts]);

  const showListings = listingsLoading || listings.length > 0;
  const showProducts = loadProducts && (productsLoading || products.length > 0);

  const ProductsPlaceholder = (
    <Box sx={{ py: { xs: 3, md: 5 }, px: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' }, gap: { xs: 1.5, sm: 2 } }}>
        {Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)}
      </Box>
    </Box>
  );

  return (
    <Box>
      {/* ── Listings section ─────────────────────────────── */}
      {showListings && (
        <Box component="section" sx={{ py: { xs: 3, md: 5 }, bgcolor: 'background.default' }}>
          <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 3 } }}>
            <SectionHeader title={t('homepage.featured.title', 'Annonces populaires')} link="/search" />
            <SixItemGrid items={listings} isProduct={false} loading={listingsLoading} />
          </Box>
        </Box>
      )}

      {/* ── Products section — lazy-loaded ───────────────── */}
      <LazySection fallback={ProductsPlaceholder}>
        {showProducts && (
          <Box
            component="section"
            sx={{
              py: { xs: 3, md: 5 },
              bgcolor: alpha(theme.palette.success.main, 0.03),
              borderTop: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
            }}
          >
            <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 3 } }}>
              <SectionHeader title={t('homepage.products.title', 'Produits à commander')} link="/search?type=products" />
              <SixItemGrid items={products} isProduct={true} loading={productsLoading} />
            </Box>
          </Box>
        )}
      </LazySection>
    </Box>
  );
};

export default HomeFeaturedSection;
